/**
 * Token Validation and Cleanup Service
 * 
 * Comprehensive token validation, expiration handling, and secure cleanup
 * for the offline-first authentication system.
 */

import { secureTokenStorage, type TokenValidationResult } from './SecureTokenStorage';
import { tokenRotationService } from './TokenRotationService';
import { authIntegrationService } from './AuthIntegrationService';

/**
 * Validation configuration
 */
export interface TokenValidationConfig {
  // Validation settings
  enableContinuousValidation: boolean;
  validationInterval: number; // milliseconds
  enableExpirationChecks: boolean;
  enableIntegrityChecks: boolean;
  
  // Cleanup settings
  enableAutoCleanup: boolean;
  cleanupOnExpiration: boolean;
  cleanupOnInvalid: boolean;
  cleanupGracePeriod: number; // milliseconds
  
  // Security settings
  enableSecurityValidation: boolean;
  maxTokenAge: number; // milliseconds
  enableAnomalyDetection: boolean;
  
  // Monitoring settings
  enableValidationLogging: boolean;
  enablePerformanceMonitoring: boolean;
  enableDebugLogging: boolean;
}

/**
 * Validation events
 */
export type TokenValidationEvent =
  | { type: 'validation_started'; data: { timestamp: string; type: 'scheduled' | 'manual' | 'initial' } }
  | { type: 'validation_completed'; data: { result: TokenValidationResult; duration: number } }
  | { type: 'token_expired'; data: { expiredAt: string; action: 'cleanup' | 'refresh' | 'none' } }
  | { type: 'token_invalid'; data: { errors: string[]; action: 'cleanup' | 'ignore' } }
  | { type: 'cleanup_performed'; data: { reason: string; itemsRemoved: number; timestamp: string } }
  | { type: 'security_violation'; data: { type: string; details: Record<string, unknown> } }
  | { type: 'anomaly_detected'; data: { anomaly: string; severity: 'low' | 'medium' | 'high' } };

export type TokenValidationEventListener = (event: TokenValidationEvent) => void;

/**
 * Validation statistics
 */
export interface ValidationStats {
  totalValidations: number;
  successfulValidations: number;
  failedValidations: number;
  expiredTokensDetected: number;
  invalidTokensDetected: number;
  cleanupsPerformed: number;
  securityViolations: number;
  averageValidationTime: number;
  lastValidationTime: string | null;
  lastCleanupTime: string | null;
}

/**
 * Token Validation Service
 */
export class TokenValidationService {
  private config: TokenValidationConfig;
  private eventListeners: Set<TokenValidationEventListener> = new Set();
  private isInitialized = false;
  
  // Validation state
  private validationTimer: NodeJS.Timeout | null = null;
  private isValidating = false;
  private stats: ValidationStats = {
    totalValidations: 0,
    successfulValidations: 0,
    failedValidations: 0,
    expiredTokensDetected: 0,
    invalidTokensDetected: 0,
    cleanupsPerformed: 0,
    securityViolations: 0,
    averageValidationTime: 0,
    lastValidationTime: null,
    lastCleanupTime: null
  };
  
  // Performance tracking
  private validationTimes: number[] = [];

  constructor(config: Partial<TokenValidationConfig> = {}) {
    this.config = {
      enableContinuousValidation: true,
      validationInterval: 300000, // 5 minutes
      enableExpirationChecks: true,
      enableIntegrityChecks: true,
      enableAutoCleanup: true,
      cleanupOnExpiration: true,
      cleanupOnInvalid: true,
      cleanupGracePeriod: 60000, // 1 minute
      enableSecurityValidation: true,
      maxTokenAge: 2592000000, // 30 days
      enableAnomalyDetection: true,
      enableValidationLogging: true,
      enablePerformanceMonitoring: true,
      enableDebugLogging: false,
      ...config
    };
  }

  /**
   * Initialize the validation service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (this.config.enableDebugLogging) {
        // Initializing Token Validation Service
      }

      // Initialize dependencies
      await secureTokenStorage.initialize();

      // Start continuous validation if enabled
      if (this.config.enableContinuousValidation) {
        this.startContinuousValidation();
      }

      // Perform initial validation
      await this.performValidation('initial');

      this.isInitialized = true;

      if (this.config.enableDebugLogging) {
        // Token Validation Service initialized successfully
      }
    } catch (error) {
      console.error('❌ Failed to initialize Token Validation Service:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: TokenValidationEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: TokenValidationEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: TokenValidationEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in token validation event listener:', error);
      }
    });
  }

  /**
   * Start continuous validation
   */
  private startContinuousValidation(): void {
    if (this.validationTimer) return;

    this.validationTimer = setInterval(async () => {
      await this.performValidation('scheduled');
    }, this.config.validationInterval);
  }

  /**
   * Stop continuous validation
   */
  private stopContinuousValidation(): void {
    if (this.validationTimer) {
      clearInterval(this.validationTimer);
      this.validationTimer = null;
    }
  }

  /**
   * Perform comprehensive token validation
   */
  async performValidation(type: 'scheduled' | 'manual' | 'initial' = 'manual'): Promise<TokenValidationResult> {
    if (this.isValidating) {
      if (this.config.enableDebugLogging) {
        // Validation already in progress, skipping
      }
      return {
        isValid: false,
        isExpired: false,
        needsRefresh: false,
        expiresIn: 0,
        errors: ['Validation in progress']
      };
    }

    this.isValidating = true;
    const startTime = Date.now();

    try {
      this.emitEvent({
        type: 'validation_started',
        data: {
          timestamp: new Date().toISOString(),
          type
        }
      });

      // Perform basic validation
      const basicValidation = await secureTokenStorage.validateTokens();
      
      // Perform additional validations
      const enhancedValidation = await this.performEnhancedValidation(basicValidation);
      
      // Handle validation results
      await this.handleValidationResults(enhancedValidation);

      // Update statistics
      this.updateValidationStats(enhancedValidation, Date.now() - startTime);

      this.emitEvent({
        type: 'validation_completed',
        data: {
          result: enhancedValidation,
          duration: Date.now() - startTime
        }
      });

      return enhancedValidation;
    } catch (error) {
      this.stats.failedValidations++;
      
      if (this.config.enableDebugLogging) {
        console.error('Token validation failed:', error);
      }

      const errorResult: TokenValidationResult = {
        isValid: false,
        isExpired: false,
        needsRefresh: false,
        expiresIn: 0,
        errors: [error instanceof Error ? error.message : 'Validation error']
      };

      this.emitEvent({
        type: 'validation_completed',
        data: {
          result: errorResult,
          duration: Date.now() - startTime
        }
      });

      return errorResult;
    } finally {
      this.isValidating = false;
    }
  }

  /**
   * Perform enhanced validation checks
   */
  private async performEnhancedValidation(basicValidation: TokenValidationResult): Promise<TokenValidationResult> {
    const enhancedErrors = [...basicValidation.errors];

    // Security validation
    if (this.config.enableSecurityValidation) {
      const securityErrors = await this.performSecurityValidation();
      enhancedErrors.push(...securityErrors);
    }

    // Integrity checks
    if (this.config.enableIntegrityChecks) {
      const integrityErrors = await this.performIntegrityChecks();
      enhancedErrors.push(...integrityErrors);
    }

    // Anomaly detection
    if (this.config.enableAnomalyDetection) {
      const anomalies = await this.detectAnomalies();
      if (anomalies.length > 0) {
        enhancedErrors.push(`Anomalies detected: ${anomalies.join(', ')}`);
      }
    }

    return {
      ...basicValidation,
      errors: enhancedErrors,
      isValid: basicValidation.isValid && enhancedErrors.length === basicValidation.errors.length
    };
  }

  /**
   * Perform security validation
   */
  private async performSecurityValidation(): Promise<string[]> {
    const errors: string[] = [];

    try {
      const tokens = await secureTokenStorage.retrieveTokens();
      
      if (tokens) {
        // Check token age
        const tokenAge = Date.now() - new Date(tokens.createdAt).getTime();
        if (tokenAge > this.config.maxTokenAge) {
          errors.push('Token exceeds maximum age');
          
          this.emitEvent({
            type: 'security_violation',
            data: {
              type: 'token_too_old',
              details: { tokenAge, maxAge: this.config.maxTokenAge }
            }
          });
          
          this.stats.securityViolations++;
        }

        // Check for suspicious patterns
        if (this.detectSuspiciousPatterns(tokens)) {
          errors.push('Suspicious token patterns detected');
          
          this.emitEvent({
            type: 'security_violation',
            data: {
              type: 'suspicious_patterns',
              details: { tokenId: tokens.userId }
            }
          });
          
          this.stats.securityViolations++;
        }
      }
    } catch (error) {
      errors.push('Security validation failed');
    }

    return errors;
  }

  /**
   * Perform integrity checks
   */
  private async performIntegrityChecks(): Promise<string[]> {
    const errors: string[] = [];

    try {
      const tokens = await secureTokenStorage.retrieveTokens();
      
      if (tokens) {
        // Check token structure
        const requiredFields = ['accessToken', 'refreshToken', 'expiresAt', 'tokenType'];
        const missingFields = requiredFields.filter(field => !tokens[field as keyof typeof tokens]);
        
        if (missingFields.length > 0) {
          errors.push(`Missing token fields: ${missingFields.join(', ')}`);
        }

        // Check token format
        if (!this.validateTokenFormat(tokens.accessToken)) {
          errors.push('Invalid access token format');
        }

        if (!this.validateTokenFormat(tokens.refreshToken)) {
          errors.push('Invalid refresh token format');
        }

        // Check expiration format
        try {
          const expiresAt = new Date(tokens.expiresAt);
          if (isNaN(expiresAt.getTime())) {
            errors.push('Invalid expiration date format');
          }
        } catch (error) {
          errors.push('Invalid expiration date');
        }
      }
    } catch (error) {
      errors.push('Integrity check failed');
    }

    return errors;
  }

  /**
   * Detect anomalies in token usage
   */
  private async detectAnomalies(): Promise<string[]> {
    const anomalies: string[] = [];

    try {
      // Check validation frequency
      if (this.stats.totalValidations > 100) {
        const recentFailureRate = this.stats.failedValidations / this.stats.totalValidations;
        if (recentFailureRate > 0.5) {
          anomalies.push('High validation failure rate');
          
          this.emitEvent({
            type: 'anomaly_detected',
            data: {
              anomaly: 'high_failure_rate',
              severity: 'high'
            }
          });
        }
      }

      // Check cleanup frequency
      if (this.stats.cleanupsPerformed > 10) {
        anomalies.push('Frequent token cleanups');
        
        this.emitEvent({
          type: 'anomaly_detected',
          data: {
            anomaly: 'frequent_cleanups',
            severity: 'medium'
          }
        });
      }

      // Check performance degradation
      if (this.config.enablePerformanceMonitoring && this.stats.averageValidationTime > 5000) {
        anomalies.push('Slow validation performance');
        
        this.emitEvent({
          type: 'anomaly_detected',
          data: {
            anomaly: 'slow_performance',
            severity: 'low'
          }
        });
      }
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Anomaly detection failed:', error);
      }
    }

    return anomalies;
  }

  /**
   * Handle validation results
   */
  private async handleValidationResults(validation: TokenValidationResult): Promise<void> {
    // Handle expired tokens
    if (validation.isExpired) {
      this.stats.expiredTokensDetected++;
      
      this.emitEvent({
        type: 'token_expired',
        data: {
          expiredAt: new Date().toISOString(),
          action: this.config.cleanupOnExpiration ? 'cleanup' : 'none'
        }
      });

      if (this.config.cleanupOnExpiration) {
        await this.performCleanup('token_expired');
      }
    }

    // Handle invalid tokens
    if (!validation.isValid && !validation.isExpired) {
      this.stats.invalidTokensDetected++;
      
      this.emitEvent({
        type: 'token_invalid',
        data: {
          errors: validation.errors,
          action: this.config.cleanupOnInvalid ? 'cleanup' : 'ignore'
        }
      });

      if (this.config.cleanupOnInvalid) {
        await this.performCleanup('token_invalid');
      }
    }

    // Handle tokens that need refresh
    if (validation.needsRefresh && validation.isValid) {
      try {
        await tokenRotationService.refreshTokens('validation_triggered');
      } catch (error) {
        if (this.config.enableDebugLogging) {
          console.error('Failed to refresh tokens during validation:', error);
        }
      }
    }
  }

  /**
   * Perform secure cleanup
   */
  async performCleanup(reason: string): Promise<void> {
    try {
      if (this.config.enableDebugLogging) {
        // Performing token cleanup
      }

      // Wait for grace period if configured
      if (this.config.cleanupGracePeriod > 0) {
        await new Promise(resolve => setTimeout(resolve, this.config.cleanupGracePeriod));
      }

      // Clear tokens
      await secureTokenStorage.clearTokens();
      
      // Notify auth integration service
      await authIntegrationService.handleLogout(reason);

      this.stats.cleanupsPerformed++;
      this.stats.lastCleanupTime = new Date().toISOString();

      this.emitEvent({
        type: 'cleanup_performed',
        data: {
          reason,
          itemsRemoved: 1,
          timestamp: new Date().toISOString()
        }
      });

      if (this.config.enableDebugLogging) {
        // Token cleanup completed
      }
    } catch (error) {
      console.error('❌ Token cleanup failed:', error);
    }
  }

  /**
   * Validate token format
   */
  private validateTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }

    // Basic JWT format check (header.payload.signature)
    const parts = token.split('.');
    if (parts.length === 3) {
      return true; // Likely a JWT
    }

    // Check for minimum length and basic format
    return token.length >= 20 && /^[A-Za-z0-9_-]+$/.test(token);
  }

  /**
   * Detect suspicious patterns
   */
  private detectSuspiciousPatterns(tokens: { accessToken: string; refreshToken: string; userId?: string }): boolean {
    // Check for identical access and refresh tokens (suspicious)
    if (tokens.accessToken === tokens.refreshToken) {
      return true;
    }

    // Check for very short tokens (suspicious)
    if (tokens.accessToken.length < 10 || tokens.refreshToken.length < 10) {
      return true;
    }

    // Check for obvious test/dummy tokens
    const suspiciousPatterns = ['test', 'dummy', 'fake', 'example', '123456'];
    const tokenContent = (tokens.accessToken + tokens.refreshToken).toLowerCase();
    
    return suspiciousPatterns.some(pattern => tokenContent.includes(pattern));
  }

  /**
   * Update validation statistics
   */
  private updateValidationStats(validation: TokenValidationResult, duration: number): void {
    this.stats.totalValidations++;
    this.stats.lastValidationTime = new Date().toISOString();

    if (validation.isValid) {
      this.stats.successfulValidations++;
    } else {
      this.stats.failedValidations++;
    }

    // Update performance metrics
    if (this.config.enablePerformanceMonitoring) {
      this.validationTimes.push(duration);
      
      // Keep only last 100 measurements
      if (this.validationTimes.length > 100) {
        this.validationTimes = this.validationTimes.slice(-100);
      }

      // Calculate average
      this.stats.averageValidationTime = 
        this.validationTimes.reduce((sum, time) => sum + time, 0) / this.validationTimes.length;
    }
  }

  /**
   * Get validation statistics
   */
  getStats(): ValidationStats {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalValidations: 0,
      successfulValidations: 0,
      failedValidations: 0,
      expiredTokensDetected: 0,
      invalidTokensDetected: 0,
      cleanupsPerformed: 0,
      securityViolations: 0,
      averageValidationTime: 0,
      lastValidationTime: null,
      lastCleanupTime: null
    };
    this.validationTimes = [];
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<TokenValidationConfig>): void {
    this.config = { ...this.config, ...updates };
    
    // Restart validation timer if interval changed
    if (updates.validationInterval && this.config.enableContinuousValidation) {
      this.stopContinuousValidation();
      this.startContinuousValidation();
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): TokenValidationConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stopContinuousValidation();
    this.eventListeners.clear();
    this.isInitialized = false;

    if (this.config.enableDebugLogging) {
      // Token Validation Service cleaned up
    }
  }
}

// Export singleton instance
export const tokenValidationService = new TokenValidationService();
